import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete as MuiAutocomplete, CircularProgress, TextField } from '@mui/material';
import debounce from 'lodash/debounce';
import uniqWith from 'lodash/uniqWith';
import isNull from 'lodash/isNull';
import { LiveFeedback } from '@/components/LiveFeedback';
import { useAriaInvalid } from '@/hooks';
import colors from '@/theme/colors';
import styles from './Autocomplete.styles';

export type Describable = {
  description: string;
  [key: string]: any;
};

export type LookupOptions = { input: string };
export type LookupCallbackFn<T extends Describable> = (results?: T[]) => void;
export type LookupFn<T extends Describable> = (
  options: LookupOptions,
  callback: LookupCallbackFn<T>
) => void;
export type SelectionState = { inputValue: string; selected: boolean };
export type RenderOptionFn<T extends Describable> = (choice: T, state: SelectionState) => ReactNode;
export type Comparer<T extends Describable> = (initial: T | null, selected: T | null) => boolean;
export type GroupByFn<T extends Describable> = (choice: T) => string;

interface Props<T extends Describable> {
  id: string;
  label?: string;
  helpText?: string;
  lookup: LookupFn<T>;
  renderOption: RenderOptionFn<T>;
  groupBy?: GroupByFn<T>;
  noOptionsText?: string;
  isEqual?: Comparer<T>;
  value: T | null;
  onChange: (value: T | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string | null;
  required?: boolean;
  'aria-describedby'?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const LIST_NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown']);

const defaultIsEqual = <T extends Describable>(a: T | null, b: T | null) => {
  if (isNull(a) || isNull(b)) return a === b;
  return a.description === b.description;
};

export const Autocomplete = <T extends Describable>({
  id,
  label,
  helpText,
  lookup,
  renderOption,
  groupBy,
  noOptionsText = 'No options',
  isEqual = defaultIsEqual,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  required,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: Props<T>) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<T[]>(value ? [value] : []);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [keyboardListNav, setKeyboardListNav] = useState(false);
  const [liveMessage, setLiveMessage] = useState('');
  const wasLoadingRef = useRef(false);

  // Callers commonly pass inline `lookup`/`isEqual` functions, so their identities
  // change on every parent render. Keeping them in refs prevents the fetch effect
  // from tearing down (and cancelling its in-flight callback) on every re-render.
  const lookupRef = useRef(lookup);
  const isEqualRef = useRef(isEqual);
  useEffect(() => {
    lookupRef.current = lookup;
    isEqualRef.current = isEqual;
  });

  const fetch = useMemo(
    () =>
      debounce(
        (opts: LookupOptions, callback: LookupCallbackFn<T>) => lookupRef.current(opts, callback),
        300
      ),
    []
  );

  useEffect(() => {
    if (!open) {
      setOptions(value ? [value] : []);
      setIsLoading(false);
      setKeyboardListNav(false);
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;

    if (value && inputValue === value.description) {
      setOptions([value]);
      setIsLoading(false);
      return undefined;
    }

    let active = true;
    setIsLoading(true);
    fetch({ input: inputValue }, results => {
      if (!active) return;
      setIsLoading(false);
      let next: T[] = value ? [value] : [];
      if (results) {
        next = uniqWith([...next, ...results], isEqualRef.current);
      }
      setOptions(next);
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetch, open, value]);

  useAriaInvalid(() => inputRef.current, error, [id]);

  useEffect(() => {
    if (isLoading && !wasLoadingRef.current) {
      setLiveMessage('Loading results');
    } else if (!isLoading && wasLoadingRef.current) {
      setLiveMessage('Results loaded');
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading]);

  const descriptionId = `${id}-description`;
  const describedByIds = [error ? descriptionId : null, ariaDescribedBy ?? null]
    .filter(Boolean)
    .join(' ');
  const rootClass = required ? `${styles.root} ${styles.required}` : styles.root;

  const handleInputKeyDownCapture = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (LIST_NAV_KEYS.has(event.key)) {
      setKeyboardListNav(true);
    }
  }, []);

  const handleListboxMouseOver = useCallback((event: React.MouseEvent<HTMLUListElement>) => {
    const target = event.target as Element | null;
    if (target?.closest('[role="option"]')) {
      setKeyboardListNav(false);
    }
  }, []);

  let helperContent;
  if (error) {
    helperContent = <span style={{ color: colors.dragon }}>{error}</span>;
  } else if (helpText) {
    helperContent = <span>{helpText}</span>;
  }

  return (
    <div className={rootClass}>
      <LiveFeedback message={liveMessage} />
      <MuiAutocomplete<T, false, false, false>
        id={id}
        options={options}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        loading={isLoading}
        autoComplete
        includeInputInList
        filterSelectedOptions={false}
        filterOptions={x => x}
        getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
        isOptionEqualToValue={(opt, val) => isEqual(opt, val)}
        groupBy={groupBy}
        noOptionsText={noOptionsText}
        value={value ?? null}
        disabled={disabled}
        onChange={(_event, newValue) => {
          onChange(newValue);
          setOpen(false);
        }}
        onBlur={onBlur}
        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
        slotProps={{
          popper: {
            ...({ 'data-keyboard-list-nav': keyboardListNav ? 'true' : undefined } as Record<
              string,
              unknown
            >),
          },
        }}
        ListboxProps={{ onMouseOver: handleListboxMouseOver }}
        renderOption={(props, option, state) => {
          const { key, ...liProps } = props as {
            key?: React.Key;
          } & React.HTMLAttributes<HTMLLIElement>;
          return (
            <li {...liProps} key={key ?? option.description}>
              <div id={`${id}-option-${option.description}`}>
                {renderOption(option, {
                  inputValue: state.inputValue,
                  selected: state.selected,
                })}
              </div>
            </li>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            variant="standard"
            fullWidth
            data-testid={testId}
            error={!!error}
            helperText={helperContent}
            inputRef={(node: HTMLInputElement | null) => {
              inputRef.current = node;
            }}
            FormHelperTextProps={{ id: descriptionId }}
            inputProps={{
              ...params.inputProps,
              'aria-label': ariaLabel,
              'aria-describedby': describedByIds || undefined,
              'aria-required': required ? 'true' : undefined,
              onKeyDownCapture: handleInputKeyDownCapture,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
};
